import {Component, computed, effect, input, signal, ChangeDetectionStrategy} from "@angular/core"
import {SceneNodes, ObjectPath} from "@cm/template-nodes"
import {TemplateTreeItemComponent} from "@app/template-editor/components/template-tree-item/template-tree-item.component"
import {CdkTreeModule, NestedTreeControl} from "@angular/cdk/tree"
import {MatTreeNestedDataSource} from "@angular/material/tree"
import {TemplateTreeSceneNodeInfoTreeItemComponent} from "@app/template-editor/components/template-tree-scene-node-info-tree-item/template-tree-scene-node-info-tree-item.component"
import {getNodeChildren, hasChildren} from "@app/template-editor/components/template-tree-scene-node-info-tree/template-tree-scene-node-info-tree-types"
import type {
    SceneNodeInfoNode,
    SceneNodeInfoTreeItem,
} from "@app/template-editor/components/template-tree-scene-node-info-tree/template-tree-scene-node-info-tree-types"

export type {SceneNodeInfoTreeItem, SceneNodeInfoNode}
export {hasChildren}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-template-tree-scene-node-info-tree",
    imports: [CdkTreeModule, TemplateTreeSceneNodeInfoTreeItemComponent],
    templateUrl: "./template-tree-scene-node-info-tree.component.html",
    styleUrl: "./template-tree-scene-node-info-tree.component.scss",
})
export class TemplateTreeSceneNodeInfoTreeComponent {
    readonly $templateTreeItem = input.required<TemplateTreeItemComponent>({alias: "templateTreeItem"})
    readonly $sceneNodeInfo = computed(() => this.$templateTreeItem().$sceneNodeInfo())
    readonly $rootNode = computed(() => {
        const sceneNodeInfo = this.$sceneNodeInfo()
        if (!sceneNodeInfo) return undefined

        const nodeMap = new Map<string, SceneNodeInfoNode>()

        const getInfoNode = (path: ObjectPath) => {
            if (path.length === 0) {
                throw new Error("Path cannot be empty")
            }

            const cacheKey = path.map((node) => node.instanceId).join("|")

            const cached = nodeMap.get(cacheKey)
            if (cached) return cached

            const newNode: SceneNodeInfoNode = {
                id: cacheKey,
                templateNode: path[path.length - 1],
                children: [],
            }

            const findSubPathIndex = (path: ObjectPath, subPath: ObjectPath) => {
                if (path.length === 0 || subPath.length > path.length) return -1
                const firstIndex = path.indexOf(subPath[0])
                if (firstIndex === -1) return -1
                for (let i = 1; i < subPath.length; i++) {
                    if (path[firstIndex + i] !== subPath[i]) {
                        return -1
                    }
                }
                return firstIndex
            }

            for (const info of sceneNodeInfo) {
                const childIndex = findSubPathIndex(info.path, path)
                if (childIndex === -1) {
                    continue
                }

                const nextIndex = childIndex + path.length

                if (nextIndex >= info.path.length) {
                    newNode.children.push(info)
                } else {
                    const childNode = getInfoNode([...path, info.path[nextIndex]])
                    if (!newNode.children.includes(childNode)) newNode.children.push(childNode)
                }
            }

            newNode.children.sort((a, b) => {
                const aIsSceneNode = SceneNodes.isSceneNode(a) ? 0 : 1
                const bIsSceneNode = SceneNodes.isSceneNode(b) ? 0 : 1
                return aIsSceneNode - bIsSceneNode
            })

            nodeMap.set(cacheKey, newNode)

            return newNode
        }

        return getInfoNode([this.$templateTreeItem().$treeNode().node])
    })
    private readonly $rootNodes = computed(() => {
        const root = this.$rootNode()
        if (!root) return []
        return getNodeChildren(root)
    })

    treeControl = new NestedTreeControl<SceneNodeInfoTreeItem, string>(getNodeChildren, {trackBy: (node) => node.id})
    readonly $dataSource = signal(new MatTreeNestedDataSource<SceneNodeInfoTreeItem>())
    readonly $hasChildren = signal(hasChildren)

    constructor() {
        effect(() => this.refreshTree())
    }

    private refreshTree() {
        this.$dataSource().data = this.$rootNodes()
    }
}
